/* eslint-disable @typescript-eslint/no-namespace */
import { make, type Brand } from "ts-brand";
import { type CustomerData } from "../commerce";
import { type ContactData } from "../contacts";
import { type MailConfiguration } from "./MailConfiguration";

/**
 * The company information of the service provider.
 */
export type Company = Omit<CustomerData, "salesContact" | "primaryContact"> & {
    /**
     * The primary contact person within the service providing company that is responsible for the customer.
     */
    primaryContact?: ContactData;
};

/**
 * The branding settings to customize the appearance of the service provider in templates.
 */
export type Branding = {
    /**
     * The greeting to display in the templates.
     */
    greeting: string;
    /**
     * The name of the person who is responsible for the greeting.
     */
    greetingBy: string;
    /**
     * The url to the logo of the service provider.
     */
    logoDataUrl: string;
    /**
     * The alt text for the logo of the service provider.
     */
    logoAltText: string;
};

/**
 * The invoicing settings of the service provider shown to customers.
 */
export type Invoicing = {
    bankName: string;
} & (
    | {
          /**
           * Optional free-text payment information (e.g. payment instructions).
           *
           * If set, `iban` and `vatNumber` may be omitted.
           */
          paymentInfo: string;
          iban?: string;
          vatNumber?: string;
      }
    | {
          /**
           * If `paymentInfo` is not set, both `iban` and `vatNumber` are required.
           */
          paymentInfo?: undefined;
          iban: string;
          vatNumber: string;
      }
);

export type ServiceProvider = {
    /**
     * The id of the service provider used to reference it in other objects.
     */
    id: ServiceProvider.Id;
    /**
     * The branding settings to customize the appearance of the service provider in templates.
     */
    branding?: Branding;
    /**
     * The company information of the service provider.
     */
    company: Company;
    /**
     * The invoicing settings of the service provider.
     */
    invoicing: Invoicing;
    /**
     * The sales contact person within the service providing company that is responsible for the customer.
     */
    salesContact?: ContactData;
    /**
     * The mail settings to configure the email sender of the service provider.
     */
    mailSettings?: MailConfiguration;
    /**
     * The url to the terms of service of the service provider.
     */
    termsOfServiceUrl?: string;
    /**
     * The url to the privacy policy of the service provider.
     */
    privacyPolicyUrl?: string;
    /**
     * The mode of the service provider to determine the environment of the service provider.
     */
    mode: "demonstration" | "production";
};

export namespace ServiceProvider {
    /**
     * The id of the provider to identify it.
     *
     * Must be a non-empty ID string.
     */
    export type Id = Brand<string, ServiceProvider>;

    /**
     * Creates a new order id.
     *
     * @param value The value to create the id from.
     * @returns The new order id.
     * @throws An error if the id is empty.
     */
    export const Id = make<Id>(value => {
        if (!value || value.trim() === "") {
            throw new Error("ServiceProvider: Property 'id' cannot be empty.");
        }
    });

    /**
     * Creates a new order id.
     *
     * @returns The new order id.
     */
    export const createId = (): Id => {
        return Id(crypto.randomUUID());
    };
}
